import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { DetailsSummaryADFQuery } from '@confluence/page-properties-report-renderer/entry-points/useDetailsSummaryADFQuery.ts';
import type {
	useDetailsSummaryADFQueryQuery$data,
	useDetailsSummaryADFQueryQuery$variables,
} from '@confluence/page-properties-report-renderer/entry-points/useDetailsSummaryADFQueryTypes.ts';
import { buildDetailsSummaryVariables } from '@confluence/page-properties-report-renderer/entry-points/buildDetailsSummaryVariables.ts';

import type {
	MacroContentNode,
	MacroPreloadContext,
	MacroPreloadHandler,
} from '../preloadMacroData.ts';

const buildVariables = (
	node: MacroContentNode,
	contentId: string,
	spaceKey: string,
): useDetailsSummaryADFQueryQuery$variables => {
	const macroParams = node.attrs?.parameters?.macroParams;
	const macroId = (node.attrs?.parameters as any)?.macroMetadata?.macroId?.value || '';

	return buildDetailsSummaryVariables({
		macroParams,
		contentId,
		spaceKey,
		macroId,
	});
};

/**
 * Preload handler for the details-summary (page properties report) macro.
 *
 * For a `detailssummary` extension node found in the ADF content, this handler
 * builds query variables from the macro parameters and fires the
 * DetailsSummaryADFQuery GraphQL query to populate the Relay store so the
 * renderer's `useDetailsSummaryADFQuery` call returns synchronously on the
 * client.
 */
export const detailsSummaryPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'detailssummary',

	routes: ['view', 'space-overview', 'edit', 'live'],

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		try {
			const variables = buildVariables(node, context.contentId, context.spaceKey);
			await query<useDetailsSummaryADFQueryQuery$data, useDetailsSummaryADFQueryQuery$variables>({
				query: DetailsSummaryADFQuery,
				variables,
				context: { single: true },
			});
		} catch {
			// Preloading is best-effort cache warming. Ignore failures here and
			// let the renderer perform the query in its normal error-handled path.
			return;
		}
	},
};
